import { Alert, Box, Stack } from '@mui/material';

import { ResponseChart } from './response-chart.component';
import { StreamingText } from './streaming-text.component';

import type { ParsedContent } from '../parse-response-content';

type ResponseContentProps = {
  parsedContent: ParsedContent[];
  isStreaming?: boolean;
};

const renderContentItem = (
  item: ParsedContent,
  index: number,
  isStreaming?: boolean,
  isLastTextItem?: boolean,
) => {
  const key = `${item.type}-${index}`;

  switch (item.type) {
    case 'thinking':
      // Thinking blocks are stripped during parsing; this case is kept
      // only as a safety net -- return null if one somehow gets through.
      return null;
    case 'text':
      return (
        <StreamingText
          key={key}
          content={item.content}
          isStreaming={isStreaming && isLastTextItem}
        />
      );
    case 'chart':
      return (
        <Box
          key={key}
          sx={{
            bgcolor: 'white',
            border: 1,
            borderColor: 'drive.border',
            borderRadius: '8px',
            p: 2,
          }}
        >
          <ResponseChart chartSpec={item.content} />
        </Box>
      );
    case 'warning':
      return (
        <Alert key={key} severity='warning'>
          {item.content}
        </Alert>
      );
    default:
      return null;
  }
};

export const ResponseContent = ({ parsedContent, isStreaming }: ResponseContentProps) => {
  if (!parsedContent?.length) {
    return null;
  }

  // Find the index of the last text item to apply streaming effect only to it
  let lastTextIndex = -1;
  for (let i = parsedContent.length - 1; i >= 0; i--) {
    if (parsedContent[i].type === 'text') {
      lastTextIndex = i;
      break;
    }
  }

  return (
    <Stack spacing={2}>
      {parsedContent.map((item, index) =>
        renderContentItem(item, index, isStreaming, index === lastTextIndex),
      )}
    </Stack>
  );
};
